import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import moment from 'moment';
import * as React from 'react';
import { toast } from 'sonner';
import { getDisplayName } from '../../../../../models/NameUtils';
import { dateInputToIsoFormat, isoToDateInputFormat } from '../../../../../models/utils/dateUtils';
import {
	useCreateDonationMutation,
	useUpdateDonationMutation,
	invalidateAllDonationFilters,
	invalidateAllDonorFilters,
	useContactFilterQuery,
} from '../../../../../queries';
import { Checkbox } from '../../../../components/Checkbox';
import { Button } from '../../../../components-v2/Button';
import { DateInput } from '../../../../components-v2/DateInput';
import {
	Dialog,
	DialogBody,
	DialogFooter,
	DialogHeader,
	DialogPopup,
	DialogTitle,
} from '../../../../components-v2/Dialog';
import { Input } from '../../../../components-v2/Input';
import { CurrencyInput } from '../../CurrencyInput';
import { TextArea } from '../../../../components-v2/TextArea';
import {
	Combobox,
	ComboboxContent,
	ComboboxEmpty,
	ComboboxInput,
	ComboboxItem,
	ComboboxList,
} from '../../../../components-v2/Combobox';
import { SimpleAutoCompleteSearchField } from '../../../../components/autocomplete/SimpleAutoCompleteSearchField';
import { DonationCampaignSearch } from '../../../../components/dataBoards/donations/DonationCampaignSearch';
import { ClearFieldIcon } from '../../../../components/svgs/icons/ClearFieldIcon';
import { TagsListWithTagsEditor } from '../../../../components/entities/tags/TagsEditor';
import { TagIcon } from '../../../../components/svgs/icons/TagIcon';
import { navigation } from '../../../../styles/colors';
import { baseStyleSheet } from '../../../../styles/styles';
import { styleSheet } from '../../EditModal/styles';
import { donationStyles } from './styles';

/** Mirrors the server's company donation roll-up fan-out cap. */
const CompanyRollUpContactLimit = 250;

export const EditDonationModal = ({
	initialDonation,
	restrictToCompanyId,
	isOpen,
	setIsOpen,
	onSave,
	onCancel,
}: {
	initialDonation: Api.IDonation;
	/**
	 * Limits the donor picker to contacts at this company. A donation always belongs to a contact, so the
	 * company view uses this to keep the choice inside the set its roll-up covers.
	 */
	restrictToCompanyId?: string;
	isOpen: boolean;
	setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
	onSave: (donation: Api.IDonation) => void;
	onCancel: () => void;
}) => {
	const [donorName, setDonorName] = React.useState(
		initialDonation.donorName ?? (initialDonation.contact ? getDisplayName(initialDonation.contact) : '')
	);
	const [contact, setContact] = React.useState<Api.IContact>(initialDonation.contact ?? null);
	const [companyContactSearch, setCompanyContactSearch] = React.useState('');
	const [amount, setAmount] = React.useState<number | undefined>(initialDonation.amount ?? 0);
	const [campaign, setCampaign] = React.useState<Api.IDonationCampaign>(initialDonation.campaign ?? null);
	const [donationDate, setDonationDate] = React.useState<Date>(
		initialDonation.date ? moment(initialDonation.date).toDate() : null
	);
	const [tags, setTags] = React.useState<string[]>(initialDonation.tagsCollection?.map(tag => tag.name) ?? []);
	const [isInKindDonation, setIsInKindDonation] = React.useState<boolean>(Boolean(initialDonation.isInKindDonation));
	const [inKindDescription, setInKindDescription] = React.useState<string>(initialDonation.inKindDescription || '');
	const [details, setDetails] = React.useState<string>(initialDonation.details || '');

	const isEditing = !!initialDonation.id;

	// The Company criterion resolves a GUID value straight to companyId, so this returns exactly the
	// contact set the company donation roll-up covers. The page size matches the server's roll-up
	// fan-out cap so the picker cannot offer fewer contacts than the totals above it are built from.
	const companyContactsQuery = useContactFilterQuery({
		enabled: !!restrictToCompanyId,
		filterRequest: {
			filter: {
				criteria: [{ property: Api.ContactFilterCriteriaProperty.Company, value: restrictToCompanyId }],
			},
		},
		pageSize: CompanyRollUpContactLimit,
	});

	const companyContacts = React.useMemo(
		() => companyContactsQuery.data?.pages.flatMap(page => page.values) ?? [],
		[companyContactsQuery.data]
	);

	// Reset form fields when initialDonation changes (for editing different donations)
	React.useEffect(() => {
		setDonorName(initialDonation.donorName ?? (initialDonation.contact ? getDisplayName(initialDonation.contact) : ''));
		setContact(initialDonation.contact ?? null);
		setCompanyContactSearch(initialDonation.contact ? getDisplayName(initialDonation.contact) : '');
		setAmount(initialDonation.amount ?? 0);
		setCampaign(initialDonation.campaign ?? null);
		setDonationDate(initialDonation.date ? moment(initialDonation.date).toDate() : null);
		setTags(initialDonation.tagsCollection?.map(tag => tag.name) ?? []);
		setIsInKindDonation(Boolean(initialDonation.isInKindDonation));
		setInKindDescription(initialDonation.inKindDescription || '');
		setDetails(initialDonation.details || '');
	}, [initialDonation]);

	const createDonationMutation = useCreateDonationMutation({
		onSuccess: (donation: Api.IDonation) => {
			toast.success('Donation successfully created');
			invalidateAllDonationFilters();
			invalidateAllDonorFilters();
			onSave(donation);
		},
	});

	const updateDonationMutation = useUpdateDonationMutation({
		onSuccess: (donation: Api.IDonation) => {
			toast.success('Donation successfully updated');
			invalidateAllDonationFilters();
			invalidateAllDonorFilters();
			onSave(donation);
		},
	});

	const save = () => {
		const campaignId = campaign?.id;

		const updatedDonation = {
			...initialDonation,
			donorName,
			amount,
			campaignId,
			date: moment(donationDate).format('YYYY-MM-DD'),
			contactId: contact.id,
			tags,
			isInKindDonation,
			inKindDescription: isInKindDonation ? inKindDescription : undefined,
			details: details || undefined,
		};

		if (isEditing) {
			updateDonationMutation.mutateAsync({ donation: updatedDonation });
			return;
		}
		createDonationMutation.mutateAsync({
			donation: updatedDonation,
		});
	};

	const cancel = () => {
		onCancel();
		setIsOpen(false);
	};

	const onContactCleared = () => {
		if (donorName === getDisplayName(contact)) {
			setDonorName('');
		}

		setContact(null);
	};

	const onContactSelected = ({ selection }: { selection: Api.IEntity }) => {
		setContact(selection);

		if (!donorName) {
			setDonorName(getDisplayName(selection));
		}
	};

	const onCompanyContactSelected = (selection: Api.IContact) => {
		if (!selection) {
			onContactCleared();
			return;
		}

		setContact(selection);

		if (!donorName) {
			setDonorName(getDisplayName(selection));
		}
	};

	const onDonationDateChange = (value: string) => {
		const iso = dateInputToIsoFormat(value);
		setDonationDate(iso ? new Date(iso) : null);
	};

	const selectDonationCampaign = (donationCampaign: Api.IDonationCampaign) => {
		setCampaign(donationCampaign);
	};

	const onCreateCampaign = (result: Api.IDonationCampaign) => {
		setCampaign(result);
	};

	const onRequestAddTag = (tag: Api.IAccountTag) => {
		if (!tags.includes(tag.name)) {
			setTags([...tags, tag.name]);
		}
	};

	const onRequestRemoveTag = (tag: Api.IAccountTag) => {
		setTags(tags.filter(t => t !== tag.name));
	};

	const canSave = !!donorName && !!contact && amount >= 0 && !!donationDate;
	const isSaving = createDonationMutation.isPending || updateDonationMutation.isPending;

	return (
		<Dialog
			open={isOpen}
			onOpenChange={open => {
				if (!open) cancel();
			}}
		>
			<DialogPopup styles={[donationStyles.popup]}>
				<DialogHeader>
					<DialogTitle>{`${isEditing ? 'Edit' : 'Add New'} Donation`}</DialogTitle>
				</DialogHeader>
				<DialogBody>
					<div className={css(styleSheet.formControl)}>
						<label className={css(styleSheet.label)}>Contact</label>

						<div className={css(styleSheet.formControlInputWrap)}>
							{restrictToCompanyId ? (
								<Combobox
									items={companyContacts}
									value={contact ?? null}
									onValueChange={onCompanyContactSelected}
									inputValue={companyContactSearch}
									onInputValueChange={setCompanyContactSearch}
									itemToStringLabel={(companyContact: Api.IContact) => getDisplayName(companyContact)}
									isItemEqualToValue={(item: Api.IContact, value: Api.IContact) => item.id === value?.id}
								>
									<ComboboxInput
										showClear
										id='donation-modal-company-contact'
										styles={[styleSheet.contactCombobox]}
										placeholder={
											companyContactsQuery.isPending
												? 'Loading contacts...'
												: companyContacts.length
													? 'Search contacts at this company'
													: 'No contacts at this company'
										}
									/>
									<ComboboxContent>
										<ComboboxEmpty>No matching contacts</ComboboxEmpty>
										<ComboboxList>
											{(companyContact: Api.IContact) => (
												<ComboboxItem key={companyContact.id} value={companyContact}>
													{getDisplayName(companyContact)}
												</ComboboxItem>
											)}
										</ComboboxList>
									</ComboboxContent>
								</Combobox>
							) : (
								<SimpleAutoCompleteSearchField
									onClear={onContactCleared}
									// TODO: Fix type mismatch between component's generic union type and our specific IEntity callback
									// @ts-ignore
									onItemSelected={onContactSelected}
									resultsLimit={5}
									pageSize={5}
									placeholder='Search'
									style={styleSheet.contactSearchField}
									type={Api.ResourceAutoCompleteViewModelType.Contact}
									initialSearchQuery={initialDonation.contact ? getDisplayName(initialDonation.contact) : ''}
								/>
							)}
						</div>
					</div>

					<div className={css(styleSheet.formControl)}>
						<label className={css(styleSheet.label)}>Donor Name / Company</label>

						<div className={css(styleSheet.formControlInputWrap)}>
							<Input
								id='donation-modal-edit-name'
								onChange={e => setDonorName(e.target.value)}
								type='text'
								value={donorName}
								placeholder='Name'
							/>
						</div>
					</div>

					<div className={css(styleSheet.formControl)}>
						<label className={css(styleSheet.label)}>Donation Amount</label>

						<div className={css(styleSheet.formControlInputWrap)}>
							<CurrencyInput
								id='donation-modal-edit-amount'
								value={amount}
								onValueChange={next => setAmount(next ?? undefined)}
							/>
						</div>
					</div>

					<div className={css(styleSheet.formControl)}>
						<div className={css(styleSheet.formControlInputWrap)}>
							<Checkbox
								checked={isInKindDonation}
								id='in-kind-donation-checkbox'
								onChange={e => setIsInKindDonation((e.target as HTMLInputElement).checked)}
							/>
							<label htmlFor='in-kind-donation-checkbox' className={css(styleSheet.checkboxLabel)}>
								This is an in-kind donation
							</label>
						</div>
					</div>

					{isInKindDonation && (
						<div className={css(styleSheet.formControl)}>
							<label className={css(styleSheet.label)}>In-Kind Description</label>

							<div className={css(styleSheet.formControlInputWrap)}>
								<Input
									id='in-kind-description-input'
									onChange={e => setInKindDescription(e.target.value)}
									type='text'
									value={inKindDescription}
									placeholder='e.g., Gift card, Volunteer time, Professional services'
								/>
							</div>
						</div>
					)}

					<div className={css(styleSheet.formControl)}>
						<label className={css(styleSheet.label)}>Campaign</label>

						<DonationCampaignSearch
							onSelectDonationCampaign={selectDonationCampaign}
							selectedCampaign={campaign}
							onClearCampaign={() => setCampaign(null)}
							onCreateCampaign={onCreateCampaign}
							className={css(baseStyleSheet.fullWidth)}
						/>
					</div>

					<div className={css(styleSheet.formControl)}>
						<label className={css(styleSheet.label)}>Donation Date</label>

						<div className={css(donationStyles.dateRow)}>
							<div className={css(donationStyles.dateInput)}>
								<DateInput
									value={donationDate ? isoToDateInputFormat(donationDate.toISOString()) : ''}
									onChange={onDonationDateChange}
								/>
							</div>
							{donationDate ? (
								<Button
									variant='ghost'
									size='icon'
									onClick={() => setDonationDate(null)}
									aria-label='Clear donation date'
								>
									<ClearFieldIcon />
								</Button>
							) : null}
						</div>
					</div>

					<div className={css(styleSheet.formControl)}>
						<label className={css(styleSheet.label)}>Tags</label>

						<div className={css(styleSheet.formControlInputWrap)}>
							<div className={css(baseStyleSheet.flex, baseStyleSheet.justifyStart)}>
								<TagIcon
									className={css(baseStyleSheet.flexShrink0, baseStyleSheet.mr4, baseStyleSheet.mt3, baseStyleSheet.w4)}
									fillColor={navigation}
								/>
								{(() => {
									// Use tagsCollection if available (when editing existing donation), otherwise create basic tag objects
									const editorTags = tags.map(tagName => {
										// Try to find the tag in the original tagsCollection to preserve color info
										const existingTag = initialDonation.tagsCollection?.find(tag => tag.name === tagName);
										return existingTag || { name: tagName, id: tagName };
									});

									return (
										<TagsListWithTagsEditor
											tagType={Api.ResourceAutoCompleteViewModelType.AccountTag}
											className={css(baseStyleSheet.flex1)}
											disabled={false}
											entity={null}
											onRequestAddTag={onRequestAddTag}
											onRequestRemoveTag={onRequestRemoveTag}
											tags={editorTags}
										/>
									);
								})()}
							</div>
						</div>
					</div>

					<div className={css(styleSheet.formControl)}>
						<label className={css(styleSheet.label)}>Details</label>

						<div className={css(styleSheet.formControlInputWrap)}>
							<TextArea
								value={details}
								onChange={e => setDetails(e.target.value)}
								placeholder='Add any additional details about this donation (e.g., check number, notes)...'
								rows={3}
							/>
						</div>
					</div>
				</DialogBody>
				<DialogFooter>
					<Button variant='secondary' onClick={cancel} disabled={isSaving}>
						Cancel
					</Button>
					<Button variant='primary' onClick={save} disabled={!canSave} isLoading={isSaving}>
						Save
					</Button>
				</DialogFooter>
			</DialogPopup>
		</Dialog>
	);
};
